import { TextFocusSelector } from '../text-focus.service';
import {
  CitationRejection,
  CitationSet,
  CodeProvisionTarget,
  EvidenceCitation,
  EvidenceCitationKind,
  EvidenceConfidence,
  EvidenceProvenance,
  EvidenceResolutionMethod,
  NormalizedBox,
  CitationPageIdentity,
} from './evidence-citation.model';

/**
 * Turns a report's raw protobuf evidence chain into validated, renderable citations (Issue #1773).
 *
 * This module is the trust boundary. Everything upstream of it is untrusted: the evidence chain is
 * written by a generation pipeline that includes a language model, and cached reports may have been
 * written by any earlier version of that pipeline. Everything downstream of it assumes a citation
 * is well-formed, points inside the authorized project, and claims no more precision than it can
 * support. Nothing else in the application re-validates, so every rule the issue asks for is
 * enforced exactly once, here, with a named {@link CitationRejectionReason} and a test per rule.
 *
 * Two rules deserve calling out because they are easy to get subtly wrong:
 *
 * **Kind is inferred, never assumed.** Citations written before #1773 have no `evidenceKind`.
 * Inference walks from most precise to least — selector, then region path, then code reference,
 * then bare page — and a weaker kind is never promoted to a stronger one. A legacy citation with a
 * page number and an excerpt is page evidence, not text evidence, however quotable the excerpt
 * looks: the excerpt is prose for a human to read, not a verified selector, and rendering it as an
 * exact highlight would be the "heuristic substring match promoted to an exact citation" the issue
 * explicitly forbids.
 *
 * **Legacy citations are renderable but not addressable.** A citation with no `citationId` gets a
 * synthesized positional one so it can still appear as a source chip. It is marked
 * `referenceable: false`, so a `[^…]` marker in prose can never resolve to it. A generator that
 * predates citation IDs cannot have written a marker against an ID this code invented afterwards,
 * so matching one would fabricate an association the report never asserted.
 */

/**
 * The raw evidence-chain entry shape, as produced by protobuf's `toObject()`.
 *
 * Declared structurally rather than imported from the generated stubs for two reasons: the stubs
 * are gitignored build output, and every field genuinely is optional on the wire, so a
 * hand-written optional shape describes reality more honestly than the generated type does. Unknown
 * extra properties are ignored, which is what keeps a newer producer from breaking an older client.
 */
export interface RawEvidenceCitation {
  pageNumber?: number;
  fileId?: string;
  sheetId?: string;
  regionPath?: string;
  excerpt?: string;
  confidence?: number;
  citationId?: string;
  claimId?: string;
  architecturalPlanId?: string;
  evidenceKind?: number;
  normalizedBox?: { xMin?: number; yMin?: number; xMax?: number; yMax?: number };
  textSelector?: {
    version?: number;
    exact?: string;
    occurrence?: number;
    prefix?: string;
    suffix?: string;
    fingerprint?: string;
  };
  codeReference?: {
    bookId?: string;
    documentSlug?: string;
    chapterSlug?: string;
    chapterTitle?: string;
    sectionId?: string;
    visibleLabel?: string;
  };
  provenance?: {
    resolutionMethod?: number;
    generatedByModel?: string;
    pageArtifactRevision?: string;
    regionHierarchyRevision?: string;
    resolvedTime?: { seconds?: number; nanos?: number };
  };
}

/**
 * The report's own identity, which citations are validated against.
 *
 * `planId` is the authorization anchor: a citation may only point at this project. `fileId` and
 * `pageNumber` describe the page whose report is open, and are used solely to fill in identity a
 * legacy citation omitted — never to override an identity a citation actually stated. That
 * distinction is what makes cross-page evidence link to its own page rather than to whichever
 * report tab happens to be in front of the reader.
 */
export interface ReportSourceContext {
  planId: string;
  fileId: string;
  pageNumber: number;
}

/** Maximum excerpt length kept for display. Longer transcripts are fetched on demand. */
const MAX_EXCERPT_LENGTH = 400;

/** Maximum selector `exact` length accepted. Guards against a pathological selector payload. */
const MAX_SELECTOR_EXACT_LENGTH = 2000;

/** Citation IDs are substituted into DOM ids and Markdown, so they may not need escaping. */
const CITATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * File IDs are positive integers everywhere else in the application, so a citation's is checked too.
 *
 * Raised by security review of PR #1824 as finding F2, which correctly noted it was not exploitable
 * today. The argument for fixing it anyway is consistency: every other identity a citation states is
 * validated here, and an unvalidated one sitting among them is the kind of gap a later change treats
 * as already checked.
 */
const FILE_ID_PATTERN = /^\d+$/;

/** Region paths are route segments: slashes separate them, and nothing may traverse upwards. */
const REGION_PATH_SEGMENT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/** Only selector schema version 1 is defined by #1744. */
const SUPPORTED_SELECTOR_VERSION = 1;

/** Wire values of the proto `EvidenceKind` enum. */
const KIND_BY_WIRE_VALUE: Readonly<Record<number, EvidenceCitationKind>> = Object.freeze({
  1: 'page',
  2: 'region',
  3: 'text',
  4: 'code',
});

/** Wire values of the proto `EvidenceCitation.Confidence` enum. */
const CONFIDENCE_BY_WIRE_VALUE: Readonly<Record<number, EvidenceConfidence>> = Object.freeze({
  1: 'high',
  2: 'medium',
  3: 'low',
});

/** Wire values of the proto `EvidenceProvenance.ResolutionMethod` enum. */
const METHOD_BY_WIRE_VALUE: Readonly<Record<number, EvidenceResolutionMethod>> = Object.freeze({
  1: 'deterministic',
  2: 'agentTrace',
  3: 'indexLookup',
  4: 'inferred',
});

/**
 * Maps one report's evidence chain into a validated {@link CitationSet}.
 *
 * Never throws and never returns a partially-built citation: a candidate is either accepted whole
 * or rejected with a reason. An empty or absent chain is a legitimate, non-exceptional outcome —
 * that is what every report cached before structured evidence existed looks like.
 */
export function mapEvidenceChain(
  rawChain: readonly RawEvidenceCitation[] | null | undefined,
  report: ReportSourceContext
): CitationSet {
  const citations: EvidenceCitation[] = [];
  const rejections: CitationRejection[] = [];
  const byId = new Map<string, EvidenceCitation>();

  (rawChain ?? []).forEach((raw, index) => {
    const mapped = mapOne(raw, report, index);
    if ('reason' in mapped) {
      rejections.push(mapped);
      return;
    }

    // Duplicate IDs are only a problem when they disagree. A generator that legitimately reuses one
    // citation across several claims emits the same record more than once, and rejecting that would
    // break requirement 7 of the issue ("one citation can be reused by multiple claims"). What must
    // be rejected is an ID that resolves to two different places, because then no reader and no
    // renderer can say which the prose meant.
    const existing = mapped.referenceable ? byId.get(mapped.id) : undefined;
    if (existing) {
      if (targetFingerprint(existing) === targetFingerprint(mapped)) return;
      rejections.push({
        reason: 'duplicateConflictingId',
        citationId: mapped.id,
        detail:
          `citation id "${mapped.id}" is used by two citations with different targets ` +
          `(${targetFingerprint(existing)} vs ${targetFingerprint(mapped)})`,
      });
      return;
    }

    citations.push(mapped);
    if (mapped.referenceable) byId.set(mapped.id, mapped);
  });

  return { citations, byId, rejections };
}

/** Maps and validates a single candidate, returning either a citation or the reason it was refused. */
function mapOne(
  raw: RawEvidenceCitation,
  report: ReportSourceContext,
  index: number
): EvidenceCitation | CitationRejection {
  const declaredId = (raw.citationId ?? '').trim();
  if (declaredId && !CITATION_ID_PATTERN.test(declaredId)) {
    return {
      reason: 'malformedCitationId',
      citationId: '',
      detail: `citation id at index ${index} is not [A-Za-z0-9_-]{1,64}`,
    };
  }

  const id = declaredId || `legacy-${index}`;
  const reject = (reason: CitationRejection['reason'], detail: string): CitationRejection => ({
    reason,
    citationId: declaredId,
    detail: `${detail} (evidence chain index ${index})`,
  });

  // Authorization first, before any effort is spent interpreting a citation we would refuse anyway.
  // An empty plan ID means "inherit the report's project", which is how every pre-#1773 citation
  // looks; a stated plan ID must match exactly.
  const declaredPlanId = (raw.architecturalPlanId ?? '').trim();
  if (declaredPlanId && declaredPlanId !== report.planId) {
    return reject(
      'unauthorizedProject',
      'citation names a project outside this report; refusing to build a link to it'
    );
  }

  const kind = inferKind(raw);
  if (!kind) {
    return reject('noTarget', 'citation carries neither a usable page number nor a code reference');
  }

  const base = {
    id,
    claimId: (raw.claimId ?? '').trim(),
    referenceable: declaredId.length > 0,
    confidence: CONFIDENCE_BY_WIRE_VALUE[raw.confidence ?? 0] ?? 'unknown',
    provenance: mapProvenance(raw.provenance),
    excerpt: (raw.excerpt ?? '').slice(0, MAX_EXCERPT_LENGTH),
  };

  if (kind === 'code') {
    const code = mapCodeReference(raw.codeReference);
    if (!code) {
      return reject('invalidCodeReference', 'code citation has no usable section or document identity');
    }
    return { ...base, kind: 'code', code };
  }

  const pageNumber = raw.pageNumber;
  if (typeof pageNumber !== 'number' || !Number.isInteger(pageNumber) || pageNumber < 1) {
    return reject('invalidPageNumber', `page number ${String(pageNumber)} is not an integer >= 1`);
  }

  // A citation that states its own file ID keeps it, which is what makes cross-file evidence point
  // at its own file. Only a citation that states none inherits the report's.
  const statedFileId = (raw.fileId ?? '').trim();
  if (statedFileId && !FILE_ID_PATTERN.test(statedFileId)) {
    return reject('invalidFileId', `file id "${statedFileId}" is not a positive integer`);
  }

  const page: CitationPageIdentity = {
    planId: report.planId,
    fileId: statedFileId || report.fileId,
    pageNumber,
    sheetId: (raw.sheetId ?? '').trim(),
  };

  if (kind === 'text') {
    const selector = mapSelector(raw.textSelector);
    if (!selector) {
      return reject('malformedSelector', 'text citation selector is missing, unsupported, or out of range');
    }
    return { ...base, kind: 'text', page, selector };
  }

  if (kind === 'region') {
    const regionPath = (raw.regionPath ?? '').trim();
    if (!isSafeRegionPath(regionPath)) {
      return reject('malformedGeometry', `region path "${regionPath}" is not a safe sequence of route segments`);
    }
    // Geometry is optional, but malformed geometry is not tolerated: silently dropping a bad box
    // would leave a citation claiming region precision it cannot deliver.
    const boxResult = mapBox(raw.normalizedBox);
    if (boxResult === 'malformed') {
      return reject('malformedGeometry', 'normalized bounding box is out of range, inverted, or degenerate');
    }
    return { ...base, kind: 'region', page, regionPath, ...(boxResult ? { box: boxResult } : {}) };
  }

  return { ...base, kind: 'page', page };
}

/**
 * Determines what a citation points at.
 *
 * An explicit `evidenceKind` is honoured, because a producer that states its kind knows better than
 * this function does. When it is unset — every pre-#1773 citation — inference runs from most
 * precise to least, in the order the proto's own documentation fixes, so producer and consumer
 * cannot drift apart. Returns `null` when there is nothing to point at.
 */
function inferKind(raw: RawEvidenceCitation): EvidenceCitationKind | null {
  const declared = KIND_BY_WIRE_VALUE[raw.evidenceKind ?? 0];
  if (declared) return declared;
  if (raw.textSelector && (raw.textSelector.exact ?? '').length > 0) return 'text';
  if ((raw.regionPath ?? '').trim().length > 0) return 'region';
  if (raw.codeReference && hasCodeIdentity(raw.codeReference)) return 'code';
  if (typeof raw.pageNumber === 'number' && raw.pageNumber >= 1) return 'page';
  return null;
}

/** Whether a raw code reference carries enough identity to build a canonical URL from. */
function hasCodeIdentity(raw: NonNullable<RawEvidenceCitation['codeReference']>): boolean {
  return (raw.documentSlug ?? '').trim().length > 0 || (raw.sectionId ?? '').trim().length > 0;
}

/**
 * Validates a code reference.
 *
 * The document slug is mandatory: it is the one part of a canonical code URL that cannot be derived
 * from anything else, and without it the builder would have to guess a host path — exactly the
 * failure mode behind the broken-link defects in #1016 and #559. The visible label falls back to
 * the section ID so a link always has wording a reader can recognize and a voice user can speak.
 */
function mapCodeReference(raw: RawEvidenceCitation['codeReference']): CodeProvisionTarget | null {
  if (!raw) return null;
  const documentSlug = (raw.documentSlug ?? '').trim();
  const sectionId = (raw.sectionId ?? '').trim();
  if (!documentSlug) return null;

  return {
    bookId: (raw.bookId ?? '').trim(),
    documentSlug,
    chapterSlug: (raw.chapterSlug ?? '').trim(),
    chapterTitle: (raw.chapterTitle ?? '').trim(),
    sectionId,
    visibleLabel: (raw.visibleLabel ?? '').trim() || sectionId || documentSlug,
  };
}

/**
 * Validates an OCR text selector against #1744's contract.
 *
 * Deliberately strict about the version: an unrecognized schema version means some later producer
 * knows something this client does not, and guessing at its meaning is how a link ends up pointing
 * somewhere plausible but wrong. Degrading to a page link is the honest outcome.
 */
function mapSelector(raw: RawEvidenceCitation['textSelector']): TextFocusSelector | null {
  if (!raw) return null;
  const exact = raw.exact ?? '';
  if (!exact || exact.length > MAX_SELECTOR_EXACT_LENGTH) return null;
  // Version 0 is proto3's default for an unset field, which is what a producer that populated the
  // rest of the selector but not the version looks like; treat it as version 1 rather than
  // discarding otherwise-valid evidence. Any other unknown version is refused.
  const version = raw.version ?? 0;
  if (version !== SUPPORTED_SELECTOR_VERSION && version !== 0) return null;

  const occurrence = raw.occurrence ?? 0;
  if (!Number.isInteger(occurrence) || occurrence < 0) return null;

  return {
    v: SUPPORTED_SELECTOR_VERSION,
    exact,
    occurrence,
    ...(raw.prefix ? { prefix: raw.prefix } : {}),
    ...(raw.suffix ? { suffix: raw.suffix } : {}),
    ...(raw.fingerprint ? { fingerprint: raw.fingerprint } : {}),
  };
}

/**
 * Converts a fractional bounding box into the {@link NormalizedBox} shape Preview focus expects.
 *
 * Returns `null` for "no geometry supplied", `'malformed'` for "geometry supplied but unusable".
 * The two must stay distinguishable: the first is a region citation that will re-resolve its box
 * from the hierarchy, the second is a citation that must be refused.
 *
 * Note the `0,0,0,0` case resolves to `null` rather than `'malformed'`, because that is precisely
 * what proto3 produces for a `NormalizedBoundingBox` message whose fields were all left at their
 * defaults, and it is indistinguishable on the wire from a genuinely absent box.
 */
function mapBox(raw: RawEvidenceCitation['normalizedBox']): NormalizedBox | null | 'malformed' {
  if (!raw) return null;
  const xMin = raw.xMin ?? 0;
  const yMin = raw.yMin ?? 0;
  const xMax = raw.xMax ?? 0;
  const yMax = raw.yMax ?? 0;
  if (xMin === 0 && yMin === 0 && xMax === 0 && yMax === 0) return null;

  const inRange = [xMin, yMin, xMax, yMax].every(value => Number.isFinite(value) && value >= 0 && value <= 1);
  if (!inRange || xMax <= xMin || yMax <= yMin) return 'malformed';

  return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin };
}

/** Maps provenance, defaulting an unrecorded resolution method to the weakest honest claim. */
function mapProvenance(raw: RawEvidenceCitation['provenance']): EvidenceProvenance {
  const seconds = raw?.resolvedTime?.seconds;
  // Sub-second precision is carried through rather than truncated. A protobuf Timestamp records
  // nanoseconds and a Date holds milliseconds, so the nanos field is the only part that can be kept;
  // dropping it silently rounded every timestamp down to a whole second. That is wrong for display,
  // and it matters more than display, because provenance timestamps are what a reader and a future
  // backfill compare when deciding whether evidence predates a re-ingestion. Rounding rather than
  // flooring, so a value is never reported as earlier than it was. Caught in review of PR #1824.
  const millis =
    typeof seconds === 'number' && seconds > 0
      ? seconds * 1000 + Math.round((raw?.resolvedTime?.nanos ?? 0) / 1_000_000)
      : null;
  return {
    // An unset method must not read as "verified". `inferred` is the safe default because it is the
    // only value that forces the UI to say the location was not directly verified.
    method: METHOD_BY_WIRE_VALUE[raw?.resolutionMethod ?? 0] ?? 'inferred',
    generatedByModel: (raw?.generatedByModel ?? '').trim(),
    pageArtifactRevision: (raw?.pageArtifactRevision ?? '').trim(),
    regionHierarchyRevision: (raw?.regionHierarchyRevision ?? '').trim(),
    ...(millis !== null ? { resolvedTime: new Date(millis) } : {}),
  };
}

/**
 * Whether a region path is safe to splice into a route.
 *
 * Region paths become URL path segments, so this rejects absolute paths, empty segments, and any
 * `.` or `..` segment that could climb out of the page's route and land the reader somewhere the
 * citation never named.
 */
function isSafeRegionPath(regionPath: string): boolean {
  if (!regionPath || regionPath.startsWith('/') || regionPath.endsWith('/')) return false;
  const segments = regionPath.split('/');
  return segments.every(segment => REGION_PATH_SEGMENT_PATTERN.test(segment) && segment !== '.' && segment !== '..');
}

/**
 * A stable string describing where a citation points, used to decide whether two citations sharing
 * an ID actually disagree. Compares destinations only — confidence, excerpt, and provenance may
 * legitimately differ between two records of the same target.
 */
function targetFingerprint(citation: EvidenceCitation): string {
  switch (citation.kind) {
    case 'code':
      return `code:${citation.code.documentSlug}#${citation.code.sectionId}`;
    case 'page':
      return `page:${citation.page.fileId}/${citation.page.pageNumber}`;
    case 'region':
      return `region:${citation.page.fileId}/${citation.page.pageNumber}/${citation.regionPath}`;
    case 'text':
      return `text:${citation.page.fileId}/${citation.page.pageNumber}/${citation.selector.exact}@${citation.selector.occurrence}`;
  }
}
